// What goes on a NameIt! results share card, and the words that go with it.
// The card never says what a word "really" is, never shows who voted, and only
// uses first names.

export const SITE_URL = 'https://name-it-ashy.vercel.app';

export function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || 'Player';
}

const label = (category) => String(category).charAt(0).toUpperCase() + String(category).slice(1);
const clean = (answer) => String(answer || '').trim();
const ordinal = (n) => `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10 > 3 || [11, 12, 13].includes(n % 100) ? 0 : n % 10] || 'th'}`;
const list = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} or ${items.at(-1)}`);

export const MAX_SHARED_PLAYERS = 10;

// Short answers read at a glance on a card; among those, the longer ones impress more.
const MAX_LEN = 12;
const bestFirst = (a, b) => b.answer.length - a.answer.length;

function shuffle(items, rng) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// Whether the judges accepted an answer: true, false, or null when it can't be known.
// The votes are the truth. Final scores are only a fallback (AI review, older rooms), and
// not once a power-up has changed them: Score Freeze and Category Sniper zero accepted
// answers, and Copycat pays 10 for a copied one.
function judgement(data, id, category, answer, letter) {
  if (answer[0].toUpperCase() !== letter) return false;
  const votes = data.reviewVotes?.[category]?.[id];
  if (votes && (votes.valid || votes.invalid)) {
    if (votes.invalid > votes.valid) return false;
    // A tie is settled by a coin flip that isn't stored.
    return votes.valid > votes.invalid ? true : null;
  }
  if ((data.powerUpEffectsLog || []).some((e) => e.outcome === 'applied')) return null;
  const score = data.scores?.[id]?.[category];
  return typeof score === 'number' ? score > 0 : null;
}

function eachAnswer(room, fn) {
  Object.entries(room.rounds || {}).forEach(([round, data]) => {
    const letter = String(data.letter || '').toUpperCase();
    const given = (category, id, answer) =>
      (data.submissions || []).some((s) => s.userId !== id && clean(s.answers?.[category]).toLowerCase() === answer.toLowerCase());
    (data.submissions || []).forEach((sub) => {
      Object.entries(sub.answers || {}).forEach(([category, raw]) => {
        const answer = clean(raw);
        if (!answer) return;
        fn({
          round: Number(round),
          letter,
          id: sub.userId,
          category,
          answer,
          onLetter: answer[0].toUpperCase() === letter,
          accepted: judgement(data, sub.userId, category, answer, letter),
          sharedWithSomeone: given(category, sub.userId, answer),
        });
      });
    });
  });
}

export function roomCard({ room, standings, meId, rng = Math.random }) {
  const ranked = standings.map((p, i) => ({ id: p.id, name: firstName(p.name), total: p.total, place: i + 1 }));
  const me = ranked.find((p) => p.id === meId) || null;
  const nameOf = Object.fromEntries((room.players || []).map((p) => [p.id, firstName(p.name)]));

  // Voted out: on the right letter, and the judges said no. Only mine: accepted, and nobody else gave it.
  const votedOut = [];
  const unique = [];
  eachAnswer(room, (a) => {
    if (a.onLetter && a.accepted === false) {
      votedOut.push({ id: a.id, name: nameOf[a.id] || 'Player', answer: a.answer, category: label(a.category), letter: a.letter, round: a.round });
    }
    if (a.id === meId && a.accepted === true && !a.sharedWithSomeone && a.answer.length <= MAX_LEN) {
      unique.push({ answer: a.answer, category: label(a.category) });
    }
  });

  const won = !!me && ranked.length > 0 && me.total === ranked[0].total;
  const winner = won ? me : ranked[0];
  const others = ranked.filter((p) => p.id !== winner?.id);
  const podium = winner ? [winner, ...others].slice(0, 3).map((p, i) => ({ ...p, place: i + 1 })) : [];
  const shortlist = shuffle(votedOut.filter((v) => v.answer.length <= MAX_LEN), rng);

  return {
    options: won ? ['won'] : shortlist.length ? ['votedOut', 'podium'] : ['podium'],
    me: me && won ? { ...me, place: 1 } : me,
    winner,
    margin: won ? me.total - (others[0]?.total ?? 0) : 0,
    unique: [...new Map(unique.map((u) => [u.answer.toLowerCase(), u])).values()].sort(bestFirst).slice(0, 3),
    votedOut: shortlist,
    judge: room.reviewMode === 'ai' || room.aiReviewEnabled === true ? 'ai' : 'room',
    podium,
    rest: ranked.slice(3),
    ranked: ranked.slice(0, MAX_SHARED_PLAYERS),
    letters: room.playedLetters?.length ? room.playedLetters : Object.values(room.rounds || {}).map((r) => r.letter).filter(Boolean),
    players: ranked.length,
    rounds: Object.keys(room.rounds || {}).length,
  };
}

export function roomCardCopy(card, kind, pick = 0) {
  if (kind === 'standings') {
    return {
      headline: 'Final standings',
      button: 'Share',
      message: `Here's how our NameIt! game ended 🏆 Think you can beat us? ${SITE_URL}`,
    };
  }
  if (kind === 'won') {
    const joint = card.margin === 0;
    const brag = card.unique.length ? ` Nobody else thought of ${list(card.unique.map((u) => u.answer))}.` : '';
    return {
      headline: joint ? 'Joint first!' : 'I won!',
      button: 'Share your win',
      message: `${joint ? 'I came joint first in our NameIt! game' : `I won our NameIt! game by ${card.margin} points`} 🏆${brag} Come and try me: ${SITE_URL}`,
    };
  }
  if (kind === 'votedOut') {
    const v = card.votedOut[pick % card.votedOut.length];
    const mine = v.id === card.me?.id;
    const ai = card.judge === 'ai';
    const who = mine ? 'I' : v.name;
    // Only what the judges did, never whether the word really fits: they may have been wrong.
    const judged = ai ? 'the judge ruled it out' : mine ? 'they voted it out' : 'we voted it out';
    const question = ai ? 'Was the judge right?' : mine ? 'Were they right?' : 'Were we right?';
    const winnerLine = !card.winner
      ? ''
      : card.winner.id === v.id
        ? ` ${who} still won.`
        : ` ${card.winner.name} won, but this is all we're talking about.`;
    return {
      headline: ai ? 'Was the judge right?' : 'Was the room right?',
      stamp: ai ? 'RULED OUT' : 'VOTED OUT',
      button: `Share ${mine ? 'your' : `${v.name}'s`} "${v.answer}"`,
      message: `${who} said ${v.answer} for ${v.category} and ${judged}.${winnerLine} ${question} 🤔 Play NameIt! with us: ${SITE_URL}`,
    };
  }
  const place = card.me ? ` I came ${ordinal(card.me.place)}. Rematch?` : '';
  return {
    headline: 'Top of the class',
    button: 'Share the results',
    message: `${card.winner?.name || 'Someone'} came top of the class in our NameIt! game 🥇${place} ${SITE_URL}`,
  };
}

const BOTS = {
  kafi: { name: 'Kafi', level: 'EASY' },
  kosi: { name: 'Kosi', level: 'MEDIUM' },
  kemi: { name: 'Kemi', level: 'HARD' },
};

export function soloCard({ userScore, botScore, bot, history = [], name, mode = 'full' }) {
  const best = [];
  history.forEach((round) => {
    Object.entries(round.userAnswers || {}).forEach(([category, raw]) => {
      const answer = clean(raw);
      if (answer && answer.length <= MAX_LEN && round.userScores?.[category] === 10) best.push({ answer, category: label(category) });
    });
  });
  return {
    result: userScore > botScore ? 'win' : userScore < botScore ? 'loss' : 'tie',
    me: firstName(name),
    botKey: bot,
    bot: BOTS[bot]?.name || 'the bot',
    userScore,
    botScore,
    margin: Math.abs(userScore - botScore),
    best: best.sort(bestFirst)[0] || null,
    letters: history.map((r) => r.letter).filter(Boolean),
    mode,
    rounds: history.length,
  };
}

export function soloCardCopy(card) {
  const url = `${SITE_URL}/solo`;
  const level = BOTS[card.botKey]?.level;
  const sub = card.botKey === 'kemi' ? 'KEMI IS THE HARDEST BOT' : `${card.bot.toUpperCase()} · ${level || ''} BOT`.replace(' ·  BOT', ' BOT');
  if (card.result === 'win') {
    const line = card.botKey === 'kemi' ? " She's the hardest bot." : '';
    return { headline: `I beat ${card.bot}`, sub, button: 'Share your win', message: `I beat ${card.bot} on NameIt! ${card.userScore} to ${card.botScore} 💪${line} Your turn: ${url}` };
  }
  if (card.result === 'tie') {
    return { headline: `I tied with ${card.bot}`, sub: 'A DRAW', button: 'Share the result', message: `I tied with ${card.bot} on NameIt! ${card.userScore} all 🤝 Your turn: ${url}` };
  }
  return {
    headline: `${card.bot} beat me`,
    sub: `BY ${card.margin} POINTS`,
    button: 'Challenge a friend',
    message: `${card.bot} beat me ${card.botScore} to ${card.userScore} on NameIt! 😤 Rematch tonight. Can you beat ${card.bot}? ${url}`,
  };
}

// Which standings rows fit in `room` lines. Everyone is shown when they fit; otherwise the
// top of the list, then a "+N more" line, then the sharer's own row, so a player lower down
// never shares a card without their name on it.
export function pickRows(list, room, markId) {
  if (room < 1 || !list.length) return [];
  if (list.length <= room) return list.map((p) => ({ type: 'row', p }));
  const at = list.findIndex((p) => p.id === markId);
  const more = (n) => ({ type: 'more', n });
  if (at < 0 || at < room - 1) {
    return [...list.slice(0, room - 1).map((p) => ({ type: 'row', p })), more(list.length - (room - 1))];
  }
  const after = list.length - at - 1;
  const top = Math.max(0, room - 2 - (after > 0 ? 1 : 0));
  const rows = list.slice(0, top).map((p) => ({ type: 'row', p }));
  rows.push(more(at - top), { type: 'row', p: list[at] });
  if (after > 0 && rows.length < room) rows.push(more(after));
  return rows;
}

const count = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// The one line that says what kind of game this was: players and rounds, or the solo mode.
export function gameFacts(card) {
  if ('botKey' in card) return `${card.mode === 'quick' ? 'Quick' : 'Full'} game · ${count(card.rounds, 'round')}`;
  return `${count(card.players, 'player')} · ${count(card.rounds, 'round')}`;
}
